/**
 * PrPickerSheet — bottom sheet that lets the user pick one PR out of
 * a list (e.g. when a workout session contains multiple PRs).
 *
 * Usage:
 *   <PrPickerSheet open={open} onClose={close} prs={prs} onPick={handlePick} />
 *
 * prs shape: Array<{ exerciseName, weight, reps, prType, prevBest? }>
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';

const PR_COLORS = {
  weight: 'text-yellow-500',
  e1rm: 'text-purple-500',
  reps: 'text-green-500',
  volume: 'text-blue-500',
  alltime: 'text-purple-500',
  progress: 'text-yellow-500',
};

export default function PrPickerSheet({ open, onClose, prs, onPick }) {
  const { t } = useTranslation();
  if (!open) return null;
  const safePrs = Array.isArray(prs) ? prs : [];
  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[70dvh] overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>{t('pr.pickOne', 'Pick a PR to share')}</SheetTitle>
            <button onClick={onClose} className="text-muted-foreground p-1" data-testid="pr-picker-close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </SheetHeader>
        <div className="mt-4 flex flex-col gap-2" data-testid="pr-picker-list">
          {safePrs.map((pr, i) => (
            <button
              key={`${pr.exerciseName}-${pr.prType}-${i}`}
              onClick={() => onPick(pr)}
              className="flex items-center gap-3 px-3 py-3 rounded-xl bg-muted/60 hover:bg-muted active:bg-muted text-left"
              data-testid={`pr-picker-item-${i}`}
            >
              <Trophy className={`h-5 w-5 ${PR_COLORS[pr.prType] || 'text-yellow-500'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{pr.exerciseName}</div>
                <div className="text-xs text-muted-foreground">
                  {pr.weight} kg{pr.reps > 1 ? ` × ${pr.reps}` : ''} · {t(`workout.pr${(pr.prType || 'weight').charAt(0).toUpperCase()}${(pr.prType || 'weight').slice(1)}`, pr.prType)}
                </div>
              </div>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
